(() => {
  document.documentElement.classList.add('aa-js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Horizon scrolls .page-wrapper at >=990px and the document below that.
  const desktopScroll = window.matchMedia('(min-width: 990px)');
  const scroller = () => (desktopScroll.matches ? document.querySelector('.page-wrapper') : null);
  const scrollTop = () => {
    const el = scroller();
    return el ? el.scrollTop : window.scrollY;
  };

  function initTopbar() {
    const group = document.getElementById('header-group');
    if (!group || group.dataset.aaTopbar) return;
    group.dataset.aaTopbar = 'true';
    group.classList.add('aa-topbar');

    const bar = group.querySelector('.aa-bar-section');
    const header = group.querySelector('.header-section');
    if (bar) {
      const setBarHeight = () => document.documentElement.style.setProperty('--aa-bar-h', `${bar.offsetHeight}px`);
      setBarHeight();
      new ResizeObserver(setBarHeight).observe(bar);
    }
    // Sticky header height for scroll-padding, so anchor jumps and focus never land under it.
    if (header) {
      const setHeadHeight = () => document.documentElement.style.setProperty('--aa-head-h', `${header.offsetHeight}px`);
      setHeadHeight();
      new ResizeObserver(setHeadHeight).observe(header);
    }

    let last = scrollTop();
    let ticking = false;
    const menuOpen = () => group.querySelector('details[open], [aria-expanded="true"], dialog[open]') !== null;
    const threshold = () => (bar?.offsetHeight || 0) + (header?.offsetHeight || 0);

    const update = () => {
      ticking = false;
      const y = scrollTop();
      // Glass look once the page has moved past the header
      group.classList.toggle('aa-topbar--scrolled', y > threshold());
      const dy = y - last;
      if (Math.abs(dy) < 6) return;
      // The bar stays visible while scrolling (owner's choice); only the glass state changes.
      group.classList.remove('aa-topbar--hidden');
      last = y;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true });
    group.addEventListener('focusin', () => group.classList.remove('aa-topbar--hidden'));
  }

  // Reveal on scroll: a soft rise with an ease-out curve (aa-base.css). Items that enter together
  // (a row of cards) cascade 70 ms apart; items already above the viewport (back navigation, anchor
  // jumps, fast scrolling) appear at once instead of animating behind the reader.
  let revealObserver;
  const showNow = (el) => {
    el.style.transition = 'none';
    el.classList.add('is-in');
    requestAnimationFrame(() => requestAnimationFrame(() => el.style.removeProperty('transition')));
  };
  function initReveal(root = document) {
    const items = root.querySelectorAll('.aa-rv:not(.is-in)');
    if (!('IntersectionObserver' in window) || reduceMotion.matches || window.Shopify?.designMode) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    revealObserver ??= new IntersectionObserver(
      (entries) => {
        let n = 0;
        for (const entry of entries) {
          const el = entry.target;
          if (!entry.isIntersecting) {
            if (entry.boundingClientRect.bottom <= 0) {
              revealObserver.unobserve(el);
              showNow(el);
            }
            continue;
          }
          revealObserver.unobserve(el);
          if (!/\baa-rv--d\d/.test(el.className)) {
            if (n) {
              el.style.transitionDelay = `${Math.min(n, 5) * 70}ms`;
              // Clear it afterwards so hover transitions on the element are never delayed.
              el.addEventListener('transitionend', () => el.style.removeProperty('transition-delay'), { once: true });
            }
            n++;
          }
          el.classList.add('is-in');
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );
    items.forEach((el) => revealObserver.observe(el));
  }

  function drawStars(canvas) {
    const w = (canvas.width = canvas.offsetWidth);
    const h = (canvas.height = canvas.offsetHeight);
    const ctx = canvas.getContext('2d');
    if (!ctx || !w || !h) return;
    for (let i = 0; i < 200; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * w, Math.random() * h * 0.75, Math.random() * 0.9 + 0.15, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.5 + 0.15})`;
      ctx.fill();
    }
    for (let i = 0; i < 10; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * w, Math.random() * h * 0.5, 1.3, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(160,210,255,.9)';
      ctx.fill();
    }
  }

  function initStars(root = document) {
    root.querySelectorAll('canvas[data-aa-stars]').forEach((canvas) => {
      drawStars(canvas);
      if (canvas.dataset.aaBound) return;
      canvas.dataset.aaBound = 'true';
      new ResizeObserver(() => drawStars(canvas)).observe(canvas);
    });
  }

  function initCounters(root = document) {
    root.querySelectorAll('[data-aa-count]').forEach((el) => {
      const target = Number(el.dataset.aaCount);
      const suffix = el.dataset.aaSuffix || '';
      if (!target || reduceMotion.matches) {
        el.textContent = `${target.toLocaleString('es-CL')}${suffix}`;
        return;
      }
      const start = performance.now();
      const step = (now) => {
        const p = Math.min((now - start) / 1600, 1);
        el.textContent = `${Math.round(target * (1 - Math.pow(1 - p, 3))).toLocaleString('es-CL')}${suffix}`;
        if (p < 1) requestAnimationFrame(step);
      };
      setTimeout(() => requestAnimationFrame(step), 800);
    });
  }

  // ---- Barra de carga entre páginas: la navegación es la normal del navegador (la página actual sigue
  // visible hasta que la siguiente está lista); la barra avanza mientras tanto y la siguiente página la
  // recibe vía sessionStorage `aa-loadbar` y la completa solo con CSS (html.aa-loadbar-arrive).
  function initPageTransition() {
    const bar = document.querySelector('.aa-loadbar');
    const html = document.documentElement;
    if (!bar || window.Shopify?.designMode) {
      html.classList.remove('aa-loadbar-on');
      return;
    }
    let current = 0;
    let raf = 0;
    let safety = 0;
    const paint = (p) => {
      current = p;
      bar.style.setProperty('--aa-loadbar-p', p.toFixed(4));
    };
    const animate = (to, ms, done) => {
      cancelAnimationFrame(raf);
      const from = current;
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / ms);
        paint(from + (to - from) * (1 - Math.pow(1 - t, 3)));
        if (t < 1) raf = requestAnimationFrame(step);
        else done?.();
      };
      raf = requestAnimationFrame(step);
    };
    // Avanza rápido al principio y cada vez más lento, sin llegar nunca al final mientras espera.
    const creep = () => {
      cancelAnimationFrame(raf);
      const from = current;
      const start = performance.now();
      const step = (now) => {
        paint(from + (0.92 - from) * (1 - Math.exp(-(now - start) / 1400)));
        raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const hide = () => {
      cancelAnimationFrame(raf);
      clearTimeout(safety);
      html.classList.remove('aa-loadbar-on');
    };

    // Volver atrás/adelante desde la memoria del navegador: la barra no debe quedar a medias.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      hide();
      html.classList.remove('aa-vt', 'aa-loadbar-arrive');
    });
    // Página que se va: la marca para el paso suave (ver aa-paginas.css).
    window.addEventListener('pageswap', (event) => {
      if (event.viewTransition) html.classList.add('aa-vt');
    });
    window.addEventListener('pagehide', () => {
      if (!html.classList.contains('aa-loadbar-on')) return;
      try {
        sessionStorage.setItem('aa-loadbar', `${Date.now()}|${current.toFixed(3)}`);
      } catch (error) {}
    });

    const begin = (url) => {
      if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return;
      if (url.pathname === location.pathname && url.search === location.search) return;
      clearTimeout(safety);
      paint(0);
      html.classList.remove('aa-loadbar-arrive');
      html.classList.add('aa-loadbar-on');
      animate(0.25, 260, creep);
      // Si no hubo cambio de página (descarga, navegación cancelada), la barra se va sola.
      safety = setTimeout(() => {
        paint(1);
        hide();
      }, 12000);
    };

    // Se decide después de los demás manejadores del clic: si uno lo canceló (menús, carrito), no hay barra.
    document.addEventListener('click', (event) => {
      if (reduceMotion.matches || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link || link.hasAttribute('download') || link.hasAttribute('data-no-transition')) return;
      if (link.target && link.target !== '_self') return;
      setTimeout(() => {
        if (!event.defaultPrevented) begin(new URL(link.href, location.href));
      }, 0);
    });
    // Formularios GET (búsqueda): misma barra.
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (reduceMotion.matches || !(form instanceof HTMLFormElement) || form.method !== 'get') return;
      if (form.target && form.target !== '_self') return;
      setTimeout(() => {
        if (!event.defaultPrevented) begin(new URL(form.action, location.href));
      }, 0);
    });
  }
  // Article: table of contents from h2s, reading progress and copy-link button.
  function initArticle(root = document) {
    const article = root.querySelector('.aa-art');
    if (!article || article.dataset.aaArt) return;
    article.dataset.aaArt = '1';

    const toc = article.querySelector('[data-aa-toc]');
    const list = toc?.querySelector('ol');
    const headings = [...article.querySelectorAll('[data-aa-body] h2, .aa-art__gear h2')];
    const slug = (text) =>
      text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const links = [];
    for (const h of headings) {
      if (!h.id) h.id = slug(h.textContent) || `seccion-${links.length + 1}`;
      if (!list) continue;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = h.textContent.trim();
      a.addEventListener('click', (event) => {
        event.preventDefault();
        h.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', a.hash);
      });
      li.append(a);
      list.append(li);
      links.push(a);
    }
    if (toc && !links.length) toc.hidden = true;
    // Collapsed on small screens so the index doesn't push the article down.
    const details = toc?.querySelector('details');
    if (details && window.matchMedia('(max-width: 989px)').matches) details.open = false;

    if (links.length && 'IntersectionObserver' in window) {
      const setActive = (id) => links.forEach((a) => a.classList.toggle('is-active', a.hash === `#${id}`));
      const io = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) setActive(visible[0].target.id);
        },
        { rootMargin: '-15% 0px -70% 0px' }
      );
      headings.forEach((h) => io.observe(h));
    }

    const bar = document.querySelector('[data-aa-progress]');
    const body = article.querySelector('.aa-art__content');
    if (bar && body) {
      let ticking = false;
      const update = () => {
        ticking = false;
        const r = body.getBoundingClientRect();
        const total = r.height - window.innerHeight * 0.6;
        const p = Math.min(1, Math.max(0, (window.innerHeight * 0.4 - r.top) / Math.max(1, total)));
        bar.style.transform = `scaleX(${p})`;
      };
      document.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { capture: true, passive: true });
      update();
    }

    // Sticky index: centred in the viewport while the header is hidden, below it when it shows.
    const aside = article.querySelector('.aa-art__aside');
    if (aside && 'ResizeObserver' in window) {
      const setCenter = () => {
        const top = Math.round((window.innerHeight - aside.offsetHeight) / 2);
        article.style.setProperty('--aa-aside-center', `${Math.min(40, Math.max(24, top))}px`);
      };
      new ResizeObserver(setCenter).observe(aside);
      window.addEventListener('resize', setCenter, { passive: true });
      setCenter();
    }
  }

  // ---- Share buttons (snippets/aa-share-buttons): Facebook/WhatsApp open in a small centred window on
  // desktop (normal link on phones), Instagram uses the share sheet when the device has one (otherwise it
  // copies the link and offers to open Instagram), and "copy link" falls back step by step.
  function initShare(root = document) {
    const coarse = window.matchMedia('(pointer: coarse)');
    const copyText = async (text) => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (error) {}
      try {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
        document.body.append(area);
        area.select();
        area.setSelectionRange(0, text.length);
        const ok = document.execCommand('copy');
        area.remove();
        return ok;
      } catch (error) {
        return false;
      }
    };
    const popup = (href) => {
      const w = 620;
      const h = 640;
      const left = Math.max(0, (window.screenX || 0) + (window.outerWidth - w) / 2);
      const top = Math.max(0, (window.screenY || 0) + (window.outerHeight - h) / 2);
      const win = window.open(href, 'aa-share', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
      if (win) {
        try {
          win.opener = null;
        } catch (error) {}
      }
      return Boolean(win);
    };

    for (const group of root.querySelectorAll('[data-aa-share-group]')) {
      if (group.dataset.aaBound) continue;
      group.dataset.aaBound = '1';
      const url = group.dataset.url || location.href;
      const title = group.dataset.title || document.title;
      const note = group.querySelector('[data-aa-share-note]');
      let noteTimer;
      const say = (html, ms = 3600) => {
        if (!note) return;
        note.innerHTML = html;
        note.hidden = false;
        note.classList.remove('is-in');
        void note.offsetWidth;
        note.classList.add('is-in');
        clearTimeout(noteTimer);
        noteTimer = setTimeout(() => (note.hidden = true), ms);
      };
      const manual = () =>
        say(`Copia este enlace: <input class="aa-share-note__input" value="${escapeHtml(url)}" readonly onfocus="this.select()">`, 9000);

      group.addEventListener('click', async (event) => {
        const btn = event.target instanceof Element ? event.target.closest('[data-aa-share]') : null;
        if (!btn || !group.contains(btn)) return;
        const kind = btn.dataset.aaShare;

        if (kind === 'facebook' || kind === 'whatsapp') {
          // Phones: let the link open the app. Desktop: small window; if the browser blocks it, the link opens a tab.
          if (coarse.matches) return;
          if (popup(btn.href)) event.preventDefault();
          return;
        }

        event.preventDefault();
        if (kind === 'copy') {
          if (await copyText(url)) {
            btn.classList.add('is-done');
            setTimeout(() => btn.classList.remove('is-done'), 2200);
            say('<strong>¡Enlace copiado!</strong> Pégalo donde quieras.');
          } else {
            manual();
          }
          return;
        }

        if (kind === 'instagram') {
          if (navigator.share) {
            try {
              await navigator.share({ title, text: title, url });
              return;
            } catch (error) {
              if (error && error.name === 'AbortError') return;
            }
          }
          const copied = await copyText(url);
          say(
            `${copied ? '<strong>Enlace copiado.</strong> ' : ''}Instagram no permite compartir enlaces desde la web: ${copied ? 'pégalo' : 'copia el enlace y pégalo'} en tu historia (sticker de enlace) o en un mensaje. <a class="aa-share-note__cta" href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" data-no-transition>Abrir Instagram ↗</a>`,
            9000
          );
          if (!copied) manual();
        }
      });
    }
  }

  // ---- Header mega navigation: hover/click panels and a pill indicator that follows the pointer.
  function initNav() {
    const nav = document.querySelector('[data-aa-nav]');
    if (!nav || nav.dataset.aaReady) return;
    nav.dataset.aaReady = '1';
    const items = [...nav.querySelectorAll('[data-aa-nav-item]')];
    const links = [...nav.querySelectorAll('.aa-nav__link, .aa-nav__promo')];
    const indicator = nav.querySelector('.aa-nav__indicator');
    let openItem = null;
    let openTimer = 0;
    let closeTimer = 0;

    const moveTo = (el) => {
      if (!indicator) return;
      if (!el || !el.classList.contains('aa-nav__link')) return indicator.classList.remove('is-on');
      const r = el.getBoundingClientRect();
      const n = nav.getBoundingClientRect();
      indicator.style.setProperty('--x', `${r.left - n.left}px`);
      indicator.style.setProperty('--w', `${r.width}px`);
      indicator.classList.add('is-on');
    };
    const rest = () => moveTo(openItem?.querySelector('.aa-nav__link') || nav.querySelector('.aa-nav__link.is-current'));

    // Keep each panel inside the viewport (16px gutter) by shifting it horizontally.
    const fit = (item) => {
      const panel = item.querySelector('.aa-nav__panel');
      if (!panel) return;
      panel.style.setProperty('--aa-shift', '0px');
      const r = panel.getBoundingClientRect();
      const over = r.right - (window.innerWidth - 16);
      const under = 16 - r.left;
      const shift = over > 0 ? -over : under > 0 ? under : 0;
      panel.style.setProperty('--aa-shift', `${Math.round(shift)}px`);
    };
    const setOpen = (item) => {
      if (openItem === item) return;
      items.forEach((i) => {
        const on = i === item;
        i.classList.toggle('is-open', on);
        i.querySelector('.aa-nav__link')?.setAttribute('aria-expanded', String(on));
      });
      openItem = item;
      if (item) fit(item);
      rest();
    };
    const close = () => setOpen(null);
    const clearTimers = () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    };

    links.forEach((link) => {
      link.addEventListener('pointerenter', () => moveTo(link));
      link.addEventListener('focus', () => moveTo(link));
    });

    items.forEach((item) => {
      const button = item.querySelector('.aa-nav__link');
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimers();
        // Switching between panels is instant; opening from closed waits a beat (hover intent).
        if (openItem) setOpen(item);
        else openTimer = setTimeout(() => setOpen(item), 90);
      });
      button?.addEventListener('click', (event) => {
        clearTimers();
        if (event.pointerType === 'mouse') setOpen(item);
        else setOpen(openItem === item ? null : item);
      });
      item.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !item.contains(event.relatedTarget) && openItem === item) close();
      });
    });
    // Plain links (Inicio, Promo) close an open panel after a short pause.
    nav.querySelectorAll(':scope > .aa-nav__list > li:not([data-aa-nav-item])').forEach((li) =>
      li.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimers();
        closeTimer = setTimeout(close, 160);
      })
    );
    // Leaving the whole menu (buttons + panels) closes it once, after a grace period.
    nav.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimers();
      closeTimer = setTimeout(() => {
        close();
        rest();
      }, 260);
    });
    nav.addEventListener('pointerenter', () => clearTimeout(closeTimer));

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !openItem) return;
      const button = openItem.querySelector('.aa-nav__link');
      close();
      button?.focus();
    });
    document.addEventListener('click', (event) => {
      if (openItem && !nav.contains(event.target)) close();
    });
    window.addEventListener('resize', () => {
      if (openItem) fit(openItem);
      rest();
    }, { passive: true });
    requestAnimationFrame(rest);
  }
  // ---- Search overlay: Shopify predictive search across products, collections, guides and pages.
  const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const formatClp = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);
  };

  function initSearch() {
    const dlg = document.querySelector('[data-aa-search]');
    if (!dlg || dlg.dataset.aaReady) return;
    dlg.dataset.aaReady = '1';
    const root = window.Shopify?.routes?.root || '/';
    const form = dlg.querySelector('[data-aa-search-form]');
    const input = dlg.querySelector('[data-aa-search-input]');
    const start = dlg.querySelector('[data-aa-search-start]');
    const results = dlg.querySelector('[data-aa-search-results]');
    const status = dlg.querySelector('[data-aa-search-status]');
    const all = dlg.querySelector('[data-aa-search-all]');
    const tabs = [...dlg.querySelectorAll('[data-aa-search-tabs] [role="tab"]')];
    const labels = { products: 'Productos', collections: 'Colecciones', articles: 'Guías', pages: 'Páginas' };
    let type = 'all';
    let data = null;
    let query = '';
    let controller;
    let timer;

    const searchUrl = (q) => {
      const map = { products: 'product', articles: 'article', pages: 'page' };
      // Synonyms reach Shopify's results page too (aa-search.js expands the query with OR)
      if (window.AASearch) return window.AASearch.searchUrl(q, map[type] ? { type: map[type] } : {});
      const params = new URLSearchParams({ q, 'options[prefix]': 'last' });
      if (map[type]) params.set('type', map[type]);
      return `${root}search?${params}`;
    };
    // Highlights the query words and their synonyms, ignoring accents ("lampara" marks "Lámpara")
    const mark = (text) => {
      const safe = escapeHtml(text);
      if (!query) return safe;
      const list = window.AASearch ? window.AASearch.highlightWords(query) : query.split(/\s+/);
      const accent = { a: '[aáàä]', e: '[eéèë]', i: '[iíìï]', o: '[oóòö]', u: '[uúùü]', n: '[nñ]' };
      const words = list
        .filter((w) => w && w.length > 1)
        .sort((a, b) => b.length - a.length)
        .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/[aeioun]/gi, (c) => accent[c.toLowerCase()] || c));
      return words.length ? safe.replace(new RegExp(`(${words.join('|')})`, 'gi'), '<mark>$1</mark>') : safe;
    };

    const openSearch = () => {
      window.AASearch?.ready().catch(() => {});
      if (!dlg.open) dlg.showModal();
      document.documentElement.classList.add('aa-search-open');
      requestAnimationFrame(() => input.focus());
    };
    const closeSearch = () => {
      if (!dlg.open || dlg.classList.contains('is-closing')) return;
      dlg.classList.add('is-closing');
      setTimeout(() => {
        dlg.classList.remove('is-closing');
        dlg.close();
      }, 140);
    };
    dlg.addEventListener('close', () => document.documentElement.classList.remove('aa-search-open'));
    dlg.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeSearch();
    });
    // Close on press (not on release) when touching the backdrop, so it reacts immediately.
    dlg.addEventListener('pointerdown', (event) => {
      if (event.target !== dlg) return;
      closeSearch();
      // The release may land on the page once the dialog is gone: swallow that one click.
      const swallow = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
      document.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', swallow, true), 700);
    });
    dlg.addEventListener('click', (event) => {
      if (event.target === dlg) closeSearch();
    });
    dlg.querySelector('[data-aa-search-close]')?.addEventListener('click', closeSearch);
    document.addEventListener('click', (event) => {
      // Buttons at the top of the phone menu drawer close it before opening search or Andix
      if (event.target instanceof Element) event.target.closest('[data-aa-drawer-close]')?.closest('header-drawer')?.close?.();
      if (event.target instanceof Element && event.target.closest('[data-aa-search-open]')) {
        event.preventDefault();
        openSearch();
      }
    });
    document.addEventListener('keydown', (event) => {
      const t = event.target;
      const typing = t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if ((event.key === '/' && !typing) || ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k')) {
        event.preventDefault();
        openSearch();
      }
    });

    const item = (href, thumb, title, sub, extra = '') =>
      `<a class="aa-search__item" href="${escapeHtml(href)}"><span class="aa-search__thumb">${thumb ? `<img src="${escapeHtml(thumb)}" alt="" loading="lazy">` : ''}</span><span class="aa-search__item-txt"><strong>${mark(title)}</strong>${sub ? `<small>${sub}</small>` : ''}</span>${extra}</a>`;
    const imgOf = (r) => r.featured_image?.url || r.image || '';
    const blocks = {
      products: (list) =>
        `<div class="aa-search__grid">${list
          .map(
            (p) =>
              `<a class="aa-search__product" href="${escapeHtml(p.url)}"><span class="aa-search__pimg">${imgOf(p) ? `<img src="${escapeHtml(imgOf(p))}" alt="" loading="lazy">` : ''}${p.available === false ? '<em>Agotado</em>' : ''}</span><strong>${mark(p.title)}</strong><span class="aa-search__price">${formatClp(p.price)}</span></a>`
          )
          .join('')}</div>`,
      collections: (list) => `<div class="aa-search__chips">${list.map((c) => `<a href="${escapeHtml(c.url)}">${mark(c.title)}</a>`).join('')}</div>`,
      articles: (list) => `<div class="aa-search__list">${list.map((a) => item(a.url, imgOf(a), a.title, `Guía${a.author ? ` · ${escapeHtml(a.author)}` : ''}`)).join('')}</div>`,
      pages: (list) => `<div class="aa-search__list">${list.map((p) => item(p.url, '', p.title, 'Página')).join('')}</div>`,
    };

    const render = () => {
      tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.type === type)));
      if (!query) {
        start.hidden = false;
        results.hidden = true;
        all.hidden = true;
        status.textContent = '';
        tabs.forEach((t) => (t.querySelector('[data-count]').textContent = ''));
        return;
      }
      if (!data) return;
      const counts = Object.fromEntries(Object.keys(labels).map((k) => [k, data[k]?.length || 0]));
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      tabs.forEach((t) => {
        const n = t.dataset.type === 'all' ? total : counts[t.dataset.type];
        t.querySelector('[data-count]').textContent = n ? String(n) : '';
      });
      start.hidden = true;
      // "Todo": the group with the strongest match goes first (e.g. "reembolso" → Páginas before Productos)
      const best = (k) => Math.max(0, ...(data[k] || []).map((r) => r.score || 0));
      const types = type === 'all' ? Object.keys(labels).sort((a, b) => best(b) - best(a)) : [type];
      const html = types
        .filter((k) => counts[k])
        .map((k) => {
          const list = type === 'all' && k === 'products' ? data[k].slice(0, 6) : data[k];
          const more = type === 'all' && counts[k] > (k === 'products' ? 6 : 99) ? `<button type="button" class="aa-search__more" data-goto="${k}">Ver ${counts[k]}</button>` : '';
          return `<section class="aa-search__group"><div class="aa-search__group-head"><p class="aa-search__heading">${labels[k]}</p>${more}</div>${blocks[k](list)}</section>`;
        })
        .join('');
      results.innerHTML = html;
      results.hidden = !html;
      status.textContent = html ? '' : `Sin resultados para “${query}”${type === 'all' ? '' : ` en ${labels[type].toLowerCase()}`}. Prueba con otra palabra.`;
      if (!html && type === 'all') start.hidden = false;
      all.hidden = false;
      all.href = searchUrl(query);
      all.firstChild.textContent = `Ver todos los resultados para “${query}” `;
      results.querySelectorAll('[data-goto]').forEach((b) =>
        b.addEventListener('click', () => {
          type = b.dataset.goto;
          render();
        })
      );
    };

    const run = async () => {
      query = input.value.trim();
      controller?.abort();
      if (!query) {
        data = null;
        render();
        return;
      }
      // Andes engine first: synonyms, plurals, typos and field priority (title > tags > type > metafields > description)
      if (window.AASearch) {
        const asked = query;
        form.classList.add('is-loading');
        try {
          await window.AASearch.ready();
          if (asked !== query) return;
          data = window.AASearch.search(query, { limit: 10, productLimit: 24 });
          render();
          form.classList.remove('is-loading');
          return;
        } catch (error) {
          form.classList.remove('is-loading');
          // Index unavailable: fall back to Shopify's predictive search below
        }
      }
      controller = new AbortController();
      form.classList.add('is-loading');
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,article,page',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'hide',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
      });
      try {
        const response = await fetch(`${root}search/suggest.json?${params}`, { signal: controller.signal });
        const json = await response.json();
        data = json?.resources?.results || {};
        render();
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = 'No pudimos buscar ahora. Intenta de nuevo.';
      } finally {
        form.classList.remove('is-loading');
      }
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(run, 220);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        results.querySelector('a')?.focus();
      }
    });
    results.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const focusables = [...results.querySelectorAll('a')];
      const i = focusables.indexOf(document.activeElement);
      event.preventDefault();
      if (event.key === 'ArrowUp' && i <= 0) return input.focus();
      focusables[Math.min(focusables.length - 1, i + (event.key === 'ArrowDown' ? 1 : -1))]?.focus();
    });
    tabs.forEach((t) =>
      t.addEventListener('click', () => {
        type = t.dataset.type;
        render();
        input.focus();
      })
    );
    dlg.querySelectorAll('[data-aa-search-term]').forEach((b) =>
      b.addEventListener('click', () => {
        input.value = b.dataset.aaSearchTerm;
        run();
        input.focus();
      })
    );
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const q = input.value.trim();
      if (!q) return;
      // Go through a link click so the page transition closes the dialog and shows the preloader.
      const link = document.createElement('a');
      link.href = searchUrl(q);
      link.hidden = true;
      document.body.append(link);
      link.click();
      link.remove();
    });
  }

  // ---- Favourites: hearts on products, saved in this browser, listed in the header drawer.
  const FAV_KEY = 'aa-favs';
  const readFavs = () => {
    try {
      const list = JSON.parse(localStorage.getItem(FAV_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  };
  const writeFavs = (list) => {
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(list));
    } catch (error) {}
  };

  function initFavorites() {
    const html = document.documentElement;
    if (html.dataset.aaFavsReady) return;
    html.dataset.aaFavsReady = '1';
    const dlg = document.querySelector('[data-aa-favs]');
    const listEl = dlg?.querySelector('[data-aa-favs-list]');
    const empty = dlg?.querySelector('[data-aa-favs-empty]');
    // Only signed-in customers can save favourites; guests get the full-screen sign-in invitation.
    const gate = document.querySelector('[data-aa-gate]');
    const loggedIn = !gate;
    const openGate = () => {
      if (!gate || gate.open) return;
      gate.classList.remove('is-closing');
      gate.showModal();
    };
    const closeGate = () => {
      if (!gate?.open || gate.classList.contains('is-closing')) return;
      gate.classList.add('is-closing');
      setTimeout(() => {
        gate.classList.remove('is-closing');
        gate.close();
      }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300);
    };
    gate?.querySelector('[data-aa-gate-close]')?.addEventListener('click', closeGate);
    gate?.addEventListener('click', (event) => {
      if (event.target === gate) closeGate();
    });
    gate?.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeGate();
    });

    const renderDrawer = (list) => {
      if (!listEl) return;
      listEl.innerHTML = list
        .map(
          (f) =>
            `<li class="aa-favs__item" data-handle="${escapeHtml(f.handle)}"><a class="aa-favs__link" href="${escapeHtml(f.url)}"><span class="aa-favs__img">${f.image ? `<img src="${escapeHtml(f.image)}" alt="" loading="lazy">` : ''}</span><span class="aa-favs__txt"><strong>${escapeHtml(f.title)}</strong><span>${escapeHtml(f.price || '')}</span></span></a><button type="button" class="aa-favs__remove" data-aa-fav-remove="${escapeHtml(f.handle)}" aria-label="Quitar ${escapeHtml(f.title)} de favoritos"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg></button></li>`
        )
        .join('');
      if (empty) empty.hidden = list.length > 0;
      markStock();
    };

    // Saved favourites can sell out later: check live stock once per page and grey out sold-out ones.
    const stock = new Map();
    const markStock = () => {
      listEl?.querySelectorAll('.aa-favs__item[data-handle]').forEach(async (li) => {
        const handle = li.dataset.handle;
        if (!stock.has(handle)) {
          const root = window.Shopify?.routes?.root || '/';
          stock.set(
            handle,
            fetch(`${root}products/${encodeURIComponent(handle)}.js`)
              .then((r) => (r.ok ? r.json() : null))
              .then((p) => (p ? p.available : null))
              .catch(() => null)
          );
        }
        li.classList.toggle('is-out', (await stock.get(handle)) === false);
      });
    };

    const sync = () => {
      const list = loggedIn ? readFavs() : [];
      const saved = new Set(list.map((f) => f.handle));
      document.querySelectorAll('[data-aa-fav]').forEach((b) => {
        const on = saved.has(b.dataset.handle);
        b.setAttribute('aria-pressed', String(on));
        b.setAttribute('aria-label', `${on ? 'Quitar' : 'Guardar'} ${b.dataset.title} ${on ? 'de' : 'en'} favoritos`);
      });
      document.querySelectorAll('[data-aa-fav-count]').forEach((c) => {
        c.textContent = String(list.length);
        c.hidden = list.length === 0;
      });
      // Header heart: filled only while there are favourites, plain outline at 0.
      document.querySelectorAll('.aa-fav-trigger').forEach((t) => {
        t.classList.toggle('has-favs', list.length > 0);
        if (!list.length) t.classList.remove('is-bump');
      });
      renderDrawer(list);
    };

    const bump = () => {
      document.querySelectorAll('.aa-fav-trigger').forEach((t) => {
        t.classList.remove('is-bump');
        void t.offsetWidth;
        t.classList.add('is-bump');
        clearTimeout(t.aaBumpTimer);
        t.aaBumpTimer = setTimeout(() => t.classList.remove('is-bump'), 750);
      });
    };

    const toggle = (button) => {
      const list = readFavs();
      const i = list.findIndex((f) => f.handle === button.dataset.handle);
      if (i >= 0) list.splice(i, 1);
      else {
        list.unshift({
          handle: button.dataset.handle,
          title: button.dataset.title,
          url: button.dataset.url,
          image: button.dataset.image,
          price: button.dataset.price,
          added: Date.now(),
        });
        button.classList.remove('is-pop');
        void button.offsetWidth;
        button.classList.add('is-pop');
        bump();
      }
      writeFavs(list.slice(0, 60));
      sync();
    };

    // Same open/close motion as the cart drawer: animate the whole dialog, finish on animationend.
    const afterAnimation = (fn, fallbackMs) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        dlg.removeEventListener('animationend', onEnd);
        fn();
      };
      const onEnd = (event) => {
        if (event.target === dlg && !event.pseudoElement) finish();
      };
      dlg.addEventListener('animationend', onEnd);
      setTimeout(finish, fallbackMs);
    };
    const openDrawer = () => {
      if (!dlg || dlg.open) return;
      sync();
      dlg.classList.remove('is-closing');
      dlg.classList.add('is-opening');
      dlg.showModal();
      afterAnimation(() => dlg.classList.remove('is-opening'), 600);
    };
    const closeDrawer = () => {
      if (!dlg?.open || dlg.classList.contains('is-closing')) return;
      dlg.classList.remove('is-opening');
      dlg.classList.add('is-closing');
      afterAnimation(() => {
        dlg.classList.remove('is-closing');
        dlg.close();
      }, 400);
    };

    // Capture phase: the heart sits on top of card links/handlers, so stop the click here.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const heart = target.closest('[data-aa-fav]');
        if (heart) {
          event.preventDefault();
          event.stopPropagation();
          if (loggedIn) toggle(heart);
          else openGate();
          return;
        }
        if (target.closest('[data-aa-fav-open]')) {
          event.preventDefault();
          if (loggedIn) openDrawer();
          else openGate();
          return;
        }
        const remove = target.closest('[data-aa-fav-remove]');
        if (remove) {
          writeFavs(readFavs().filter((f) => f.handle !== remove.dataset.aaFavRemove));
          sync();
        }
      },
      true
    );
    dlg?.querySelector('[data-aa-favs-close]')?.addEventListener('click', closeDrawer);
    dlg?.addEventListener('click', (event) => {
      if (event.target === dlg) closeDrawer();
    });
    dlg?.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDrawer();
    });
    window.addEventListener('storage', (event) => {
      if (event.key === FAV_KEY) sync();
    });
    // Cards re-rendered by Horizon (filters, pagination, recommendations) need their state again.
    let pendingSync = false;
    new MutationObserver((mutations) => {
      if (pendingSync) return;
      if (!mutations.some((m) => [...m.addedNodes].some((n) => n instanceof Element && (n.matches('[data-aa-fav]') || n.querySelector('[data-aa-fav]'))))) return;
      pendingSync = true;
      requestAnimationFrame(() => {
        pendingSync = false;
        sync();
      });
    }).observe(document.body, { childList: true, subtree: true });
    sync();
  }

  // ---- Cart bubble waits for the "fly to cart" animation (ours or Horizon's) to land before the
  // number changes, so the product reaches the icon first and then the count updates.
  function initCartBubbleSync() {
    const flying = () => window.aaFlightPending || document.querySelector('fly-to-cart, .aa-fly');
    const patch = (icon) => {
      if (icon.aaBubblePatched || typeof icon.renderCartBubble !== 'function') return;
      icon.aaBubblePatched = true;
      const original = icon.renderCartBubble;
      icon.renderCartBubble = async (count, animate = true) => {
        const start = Date.now();
        while (flying() && Date.now() - start < 2000) await new Promise((resolve) => setTimeout(resolve, 60));
        return original.call(icon, count, animate);
      };
    };
    // Pop the bubble whenever its number goes up (grow + glow ring), whatever code path updated it.
    const watch = (icon) => {
      if (icon.aaBubbleWatched) return;
      icon.aaBubbleWatched = true;
      const read = () => parseInt(icon.querySelector('.cart-bubble__text-count')?.textContent || '0', 10) || 0;
      let last = read();
      new MutationObserver(() => {
        const now = read();
        if (now > last) {
          const bubble = icon.querySelector('.cart-bubble');
          if (bubble) {
            bubble.classList.remove('aa-bubble-pop');
            void bubble.offsetWidth;
            bubble.classList.add('aa-bubble-pop');
            clearTimeout(bubble.aaPopTimer);
            bubble.aaPopTimer = setTimeout(() => bubble.classList.remove('aa-bubble-pop'), 900);
          }
        }
        last = now;
      }).observe(icon, { childList: true, characterData: true, subtree: true });
    };
    customElements.whenDefined('cart-icon').then(() =>
      document.querySelectorAll('cart-icon').forEach((icon) => {
        patch(icon);
        watch(icon);
      })
    );
  }

  // ---- Cart drawer: also close when clicking anywhere outside it (desktop has no backdrop).
  function initCartDrawerDismiss() {
    document.addEventListener('click', (event) => {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer || !drawer.hasAttribute('open') || typeof drawer.close !== 'function') return;
      const target = event.target;
      if (!(target instanceof Element) || !target.isConnected) return;
      const path = event.composedPath();
      const panel = drawer.querySelector('dialog');
      if (panel && path.includes(panel)) return;
      if (target.closest('[aria-controls="cart-drawer"], [on\\:click*="cart-drawer"], dialog[open], .jdgm-write-review-modal')) return;
      drawer.close();
    });
  }

  // ---- Andes product cards: round "add to bag" button.
  // Product image flies from the card to the header cart icon (header revealed first if hidden).
  async function flyFromCard(source, image) {
    if (reduceMotion.matches) return;
    const group = document.getElementById('header-group');
    if (group?.classList.contains('aa-topbar--hidden')) {
      group.classList.remove('aa-topbar--hidden');
      await new Promise((resolve) => setTimeout(resolve, 320));
    }
    const icon = document.querySelector('.header-actions__cart-icon');
    if (!icon) return;
    const a = source.getBoundingClientRect();
    const b = icon.getBoundingClientRect();
    const from = { x: a.left + a.width / 2, y: a.top + a.height / 2 };
    const dx = b.left + b.width / 2 - from.x;
    const dy = b.top + b.height / 2 - from.y;
    const ghost = document.createElement('div');
    ghost.className = 'aa-fly';
    ghost.setAttribute('aria-hidden', 'true');
    if (image) ghost.style.backgroundImage = `url("${image}")`;
    ghost.style.left = `${from.x}px`;
    ghost.style.top = `${from.y}px`;
    document.body.append(ghost);
    const lift = Math.min(-60, dy * 0.35 - 100);
    await ghost
      .animate(
        [
          { transform: 'translate(-50%,-50%) scale(.3)', opacity: 0 },
          { transform: 'translate(-50%,-50%) scale(1)', opacity: 1, offset: 0.15 },
          { transform: `translate(calc(-50% + ${dx * 0.45}px), calc(-50% + ${lift}px)) scale(.85) rotate(-8deg)`, offset: 0.55 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(.22) rotate(10deg)`, opacity: 0.9 },
        ],
        { duration: 900, easing: 'cubic-bezier(.45,0,.25,1)', fill: 'forwards' }
      )
      .finished.catch(() => {});
    ghost.remove();
    icon.classList.remove('aa-cart-bump');
    void icon.offsetWidth;
    icon.classList.add('aa-cart-bump');
    setTimeout(() => icon.classList.remove('aa-cart-bump'), 900);
  }

  function initQuickAdd() {
    const root = window.Shopify?.routes?.root || '/';
    document.addEventListener(
      'click',
      async (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-aa-quick-add]') : null;
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        if (button.classList.contains('is-loading')) return;
        button.classList.remove('is-added');
        button.classList.add('is-loading');
        window.aaFlightPending = true;
        let events = null;
        let deferred = null;
        try {
          // Same event Horizon's product forms use, so the cart bubble and drawer refresh themselves.
          events = await import('@shopify/events');
          deferred = events.CartLinesUpdateEvent.createPromise();
          button.dispatchEvent(
            new events.CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: [{ merchandiseId: button.dataset.variantId, quantity: 1 }],
              promise: deferred.promise,
            })
          );
        } catch (error) {
          events = null;
        }
        try {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }),
          });
          const added = await response.json();
          const cart = await fetch(`${root}cart.js`).then((r) => r.json());
          const failed = !response.ok || Boolean(added.status);
          if (deferred && events) {
            deferred.resolve({
              cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { didError: failed, source: 'aa-product-card', itemCount: 1, items: cart.items },
            });
          }
          button.classList.remove('is-loading');
          if (failed) {
            window.aaFlightPending = false;
            button.title = added.description || added.message || 'No se pudo agregar';
            return;
          }
          button.classList.add('is-added');
          setTimeout(() => button.classList.remove('is-added'), 2200);
          await flyFromCard(button.closest('.aa-card__media') || button, button.dataset.image);
        } catch (error) {
          deferred?.reject(error);
          button.classList.remove('is-loading');
        } finally {
          window.aaFlightPending = false;
        }
      },
      true
    );
  }

  // ---- Collection toolbar: live search + quick filter chips over the cards on the page.
  function initCollectionTools() {
    const tools = document.querySelector('[data-aa-ctools]');
    if (!tools || tools.dataset.aaReady) return;
    tools.dataset.aaReady = '1';
    const input = tools.querySelector('[data-aa-ctools-input]');
    const clear = tools.querySelector('[data-aa-ctools-clear]');
    const count = tools.querySelector('[data-aa-ctools-count]');
    const empty = tools.querySelector('[data-aa-ctools-empty]');
    const freeFrom = Number(tools.dataset.freeFrom) || 49990;
    const flags = new Set();
    let range = null;
    const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

    const cards = () =>
      [...document.querySelectorAll('main product-card')].map((card) => {
        const item = card.closest('li') || card;
        if (!item.dataset.aaText) {
          const title = card.querySelector('.product-card__link')?.getAttribute('aria-label') || card.textContent;
          item.dataset.aaText = norm(title);
          item.dataset.aaHandle = (card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '').match(/\/products\/([^/?#]+)/)?.[1] || '';
          if (item.parentElement) item.dataset.aaOrder = String([...item.parentElement.children].indexOf(item));
          const priceEl = card.querySelector('.price-item__group.price, .price');
          item.dataset.aaPrice = String(Number((priceEl?.textContent || '').replace(/[^\d]/g, '')) || 0);
          item.dataset.aaSale = card.querySelector('.aa-badge--sale, .compare-at-price') ? '1' : '0';
          item.dataset.aaOut = card.querySelector('.aa-badge--out') ? '1' : '0';
        }
        return item;
      });

    // Andes engine (synonyms, plurals, typos, field priority); title-only matching while the index loads
    let engine = false;
    if (window.AASearch) window.AASearch.ready().then(() => { engine = true; if (input.value.trim()) apply(); }).catch(() => {});
    const apply = () => {
      const words = norm(input.value).split(/\s+/).filter(Boolean);
      const list = cards();
      const scores = engine && words.length ? window.AASearch.scoreProducts(input.value, { available: false }) : null;
      let shown = 0;
      for (const item of list) {
        const price = Number(item.dataset.aaPrice);
        let ok = scores ? scores.has(item.dataset.aaHandle) : words.every((w) => item.dataset.aaText.includes(w));
        if (ok && flags.has('available')) ok = item.dataset.aaOut !== '1';
        if (ok && flags.has('sale')) ok = item.dataset.aaSale === '1';
        if (ok && flags.has('free')) ok = price >= freeFrom;
        if (ok && range) ok = price >= range[0] && (range[1] === null || price <= range[1]);
        item.classList.toggle('aa-ctools-hidden', !ok);
        if (ok) {
          shown += 1;
          item.style.setProperty('--aa-ct-i', String(Math.min(shown, 12)));
        }
      }
      // Most relevant first while searching, via CSS order (the grid is display:grid). The DOM order must stay
      // as loaded: Horizon's infinite scroll asks for the page after the last card's data-page, so moving a
      // page-1 card to the end made it load page 2 again (duplicated products).
      if (scores) {
        const ranked = list.filter((item) => !item.classList.contains('aa-ctools-hidden'));
        ranked.sort((x, y) => (scores.get(y.dataset.aaHandle) || 0) - (scores.get(x.dataset.aaHandle) || 0) || Number(x.dataset.aaOrder) - Number(y.dataset.aaOrder));
        ranked.forEach((item, i) => {
          item.style.order = String(i);
          item.style.setProperty('--aa-ct-i', String(Math.min(i + 1, 12)));
        });
      } else list.forEach((item) => item.style.removeProperty('order'));
      const active = words.length || flags.size || range;
      count.textContent = active ? `${shown} de ${list.length} productos` : `${list.length} productos`;
      count.classList.toggle('is-active', Boolean(active));
      // Horizon's "N artículos" (and the drawer's "Ver N artículos") follow the search and quick filters;
      // with none active they keep the server's count, which already reflects Disponibilidad, Precio, etc.
      const plural = (n) => `${n} artículo${n === 1 ? '' : 's'}`;
      document.querySelectorAll('main .products-count-wrapper [role="status"], main .facets__see-results').forEach((el) => {
        const now = el.textContent.trim();
        if (now !== el.dataset.aaSet) el.dataset.aaBase = now; // fresh text from Horizon's re-render
        const see = el.classList.contains('facets__see-results');
        const text = active ? (see ? `Ver ${plural(shown)}` : plural(shown)) : el.dataset.aaBase;
        if (now !== text) el.textContent = text;
        el.dataset.aaSet = text;
      });
      empty.hidden = !(active && shown === 0 && list.length);
      clear.hidden = !input.value;
    };

    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(apply, 120);
    });
    clear.addEventListener('click', () => {
      input.value = '';
      apply();
      input.focus();
    });
    tools.querySelectorAll('[data-aa-filter]').forEach((chip) =>
      chip.addEventListener('click', () => {
        const key = chip.dataset.aaFilter;
        if (flags.has(key)) flags.delete(key);
        else flags.add(key);
        chip.setAttribute('aria-pressed', String(flags.has(key)));
        apply();
      })
    );
    const priceChips = [...tools.querySelectorAll('[data-aa-price]')];
    priceChips.forEach((chip) =>
      chip.addEventListener('click', () => {
        const on = chip.getAttribute('aria-pressed') !== 'true';
        priceChips.forEach((c) => c.setAttribute('aria-pressed', 'false'));
        if (on) {
          const [min, max] = chip.dataset.aaPrice.split('-');
          range = [Number(min) || 0, max ? Number(max) : null];
          chip.setAttribute('aria-pressed', 'true');
        } else range = null;
        apply();
      })
    );
    tools.querySelector('[data-aa-ctools-reset]')?.addEventListener('click', () => {
      input.value = '';
      flags.clear();
      range = null;
      tools.querySelectorAll('[aria-pressed]').forEach((c) => c.setAttribute('aria-pressed', 'false'));
      apply();
    });
    // "En oferta" toggle placed right after Horizon's filters (next to "Precio"); re-mounted when
    // Horizon re-renders the facets.
    const mountSale = () => {
      const wrap = document.querySelector('main .facets__filters-wrapper');
      if (!wrap || wrap.querySelector('[data-aa-sale-toggle]')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'aa-sale-toggle';
      button.dataset.aaSaleToggle = '';
      button.setAttribute('aria-pressed', String(flags.has('sale')));
      button.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="8" cy="8" r="1.5"/></svg><span>En oferta</span>';
      button.addEventListener('click', () => {
        if (flags.has('sale')) flags.delete('sale');
        else flags.add('sale');
        button.setAttribute('aria-pressed', String(flags.has('sale')));
        apply();
      });
      const list = wrap.querySelector('.facets__overflow-list');
      if (list) list.after(button);
      else wrap.append(button);
    };
    tools.querySelector('[data-aa-ctools-reset]')?.addEventListener('click', () => {
      document.querySelector('[data-aa-sale-toggle]')?.setAttribute('aria-pressed', 'false');
    });
    // Desktop: the search box joins Horizon's filter row (search · Disponibilidad · Precio · En oferta).
    // It is detached from the facets form so typing never triggers a filter request; re-mounted after
    // Horizon re-renders the facets. Phones keep it in its own row above the filters.
    const searchBox = tools.querySelector('.aa-ctools__search');
    const searchHome = tools.querySelector('.aa-ctools__row');
    const desktop = window.matchMedia('(min-width: 750px)');
    if (searchBox) {
      input.setAttribute('form', 'aa-ctools-standalone');
      ['input', 'change', 'keyup'].forEach((type) => searchBox.addEventListener(type, (e) => e.stopPropagation()));
      searchBox.addEventListener('keydown', (e) => {
        e.stopPropagation();
        if (e.key === 'Enter') e.preventDefault();
      });
    }
    const mountSearch = () => {
      if (!searchBox) return;
      const wrap = document.querySelector('main .facets__filters-wrapper');
      if (desktop.matches && wrap && wrap.offsetParent !== null) {
        if (searchBox.parentElement !== wrap) wrap.prepend(searchBox);
        tools.classList.add('aa-ctools--inline');
      } else if (searchBox.parentElement !== searchHome && searchHome) {
        searchHome.prepend(searchBox);
        tools.classList.remove('aa-ctools--inline');
      }
    };
    desktop.addEventListener('change', mountSearch);
    // "Nada coincide" notice sits below the search/filter row, right where the products would be.
    const mountEmpty = () => {
      const target = document.querySelector('main ul.product-grid, main .main-collection-grid__empty');
      if (target && target.previousElementSibling !== empty) target.before(empty);
    };
    mountSale();
    mountSearch();
    mountEmpty();
    const main = document.querySelector('main');
    if (main) {
      let remount = false;
      new MutationObserver(() => {
        if (remount) return;
        remount = true;
        requestAnimationFrame(() => {
          remount = false;
          mountSale();
          mountSearch();
          mountEmpty();
        });
      }).observe(main, { childList: true, subtree: true });
    }

    // Horizon re-renders the grid on filter/sort/pagination: re-apply to the new cards.
    const grid = document.querySelector('main .product-grid, main [class*="product-grid"]');
    if (grid) {
      let pending = false;
      new MutationObserver(() => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(() => {
          pending = false;
          apply();
        });
      }).observe(grid, { childList: true, subtree: true });
    }
    apply();
  }

  // Reviews: our CTA opens Judge.me's own form (its button is hidden in the restyle).
  function initReviews(root = document) {
    for (const cta of root.querySelectorAll('[data-aa-review-open]')) {
      if (cta.dataset.aaBound) continue;
      cta.dataset.aaBound = '1';
      cta.addEventListener('click', () => {
        const section = cta.closest('.aa-reviews');
        const button = section?.querySelector('.jm-action-buttons__button, .jdgm-write-rev-link');
        if (button) button.click();
        else section?.querySelector('.aa-reviews__widget')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  // ---- Article comments (sections/aa-comments): compact composer that expands on focus, one reply per
  // top-level comment ("[re:<id>]" prefix, the form moves under that comment), "Nombre Apellido" joined
  // into comment[author], and a scroll back to the section after posting (also after the captcha page).
  function initComments(root = document) {
    root.querySelectorAll('[data-aa-cm]:not([data-aa-ready])').forEach((section) => {
      section.dataset.aaReady = '1';
      const notice = section.querySelector('[data-aa-cm-done], [data-aa-cm-error]');
      const cameBack = /comment/i.test(location.hash) || /comentarios/.test(location.hash);
      if (notice || cameBack) {
        setTimeout(() => {
          (notice || section).scrollIntoView({ block: notice ? 'center' : 'start', behavior: 'instant' });
          if (notice) {
            notice.setAttribute('tabindex', '-1');
            notice.focus({ preventScroll: true });
          }
        }, 80);
      }

      // Thread line from a comment's avatar down to its reply's avatar (measured, so it never overshoots).
      const threads = () => {
        section.querySelectorAll('.aa-cm__item').forEach((item) => {
          const own = item.querySelector(':scope > .aa-cm__avatar');
          const replyAvatar = item.querySelector('.aa-cm__reply .aa-cm__avatar');
          item.classList.toggle('has-thread', Boolean(own && replyAvatar));
          if (!own || !replyAvatar) return;
          const base = item.getBoundingClientRect().top;
          const top = own.getBoundingClientRect().bottom - base + 4;
          const end = replyAvatar.getBoundingClientRect();
          item.style.setProperty('--aa-thread-top', `${Math.round(top)}px`);
          item.style.setProperty('--aa-thread-h', `${Math.max(0, Math.round(end.top + end.height / 2 - base - top))}px`);
        });
      };
      threads();
      if ('ResizeObserver' in window) new ResizeObserver(threads).observe(section);
      const form = section.querySelector('.aa-cm__form');
      const body = form?.querySelector('[data-aa-cm-body]');
      if (!form || !body) return;
      const home = section.querySelector('[data-aa-cm-home]');
      const count = form.querySelector('[data-aa-cm-count]');
      const first = form.querySelector('[data-aa-cm-first]');
      const last = form.querySelector('[data-aa-cm-last]');
      const submit = form.querySelector('[data-aa-cm-submit]');
      const label = form.querySelector('[data-aa-cm-label]');
      const replying = form.querySelector('[data-aa-cm-replying]');
      const replyTo = form.querySelector('[data-aa-cm-replyto]');
      const placeholder = body.placeholder;
      let replyId = '';

      // The real fields are hidden inputs filled on submit (idempotent, even if the form is re-submitted).
      const realAuthor = Object.assign(document.createElement('input'), { type: 'hidden', name: 'comment[author]' });
      const realBody = Object.assign(document.createElement('input'), { type: 'hidden', name: 'comment[body]' });
      first?.removeAttribute('name');
      body.removeAttribute('name');
      form.append(realAuthor, realBody);

      const open = () => form.classList.add('is-open');
      const fit = () => {
        body.style.height = 'auto';
        body.style.height = `${Math.min(body.scrollHeight + 2, 260)}px`;
      };
      const sync = () => {
        if (!count) return;
        count.textContent = String(body.value.length);
        count.parentElement?.classList.toggle('is-near', body.value.length > 1350);
      };
      body.addEventListener('focus', open);
      body.addEventListener('input', () => {
        open();
        fit();
        sync();
      });
      if (body.value.trim() || section.querySelector('[data-aa-cm-error]')) open();
      sync();
      form.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!form.contains(document.activeElement) && !body.value.trim() && !replyId && !form.classList.contains('is-tried')) {
            form.classList.remove('is-open');
            body.style.height = '';
          }
        }, 160);
      });

      const showReplyButtons = () => section.querySelectorAll('[data-aa-cm-reply]').forEach((b) => (b.hidden = false));
      const stopReply = () => {
        replyId = '';
        home?.append(form);
        if (replying) replying.hidden = true;
        body.placeholder = placeholder;
        showReplyButtons();
      };
      section.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const btn = target?.closest('[data-aa-cm-reply]');
        if (btn) {
          const slot = section.querySelector(`[data-aa-cm-slot="${btn.dataset.aaCmReply}"]`);
          if (!slot) return;
          replyId = btn.dataset.aaCmReply;
          showReplyButtons();
          btn.hidden = true;
          slot.append(form);
          if (replying) replying.hidden = false;
          if (replyTo) replyTo.textContent = btn.dataset.author || '';
          body.placeholder = `Responde a ${btn.dataset.author || 'este comentario'}…`;
          open();
          body.focus({ preventScroll: true });
          form.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'instant' : 'smooth' });
          return;
        }
        if (target?.closest('[data-aa-cm-cancel]')) stopReply();
      });

      // Red marks only after a publish attempt.
      submit?.addEventListener('click', () => form.classList.add('is-tried'));
      form.addEventListener('submit', () => {
        realAuthor.value = [first?.value.trim(), last?.value.trim()].filter(Boolean).join(' ').slice(0, 80);
        realBody.value = `${replyId ? `[re:${replyId}] ` : ''}${body.value.trim()}`;
        submit?.classList.add('is-sending');
        if (label) label.textContent = 'Publicando…';
      });
      // Coming back from the bfcache: the button must not stay in "sending".
      window.addEventListener('pageshow', () => {
        submit?.classList.remove('is-sending');
        if (label) label.textContent = 'Publicar';
      });
    });
  }

  // ---- Shopify's captcha page (/challenge, rendered inside this layout): friendlier header and steps,
  // a way back, and it continues by itself as soon as the "I am human" check is solved.
  function initChallenge() {
    const box = document.querySelector('.shopify-challenge__container');
    if (!box || box.dataset.aaReady) return;
    box.dataset.aaReady = '1';
    document.documentElement.classList.add('aa-challenge');
    const form = box.querySelector('form');
    const button = box.querySelector('.shopify-challenge__button, [type="submit"]');
    const head = document.createElement('div');
    head.className = 'aa-ch__head';
    head.innerHTML =
      '<span class="aa-ch__ico" aria-hidden="true"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 4.5 6v5.5c0 4.6 3.2 8.4 7.5 9.5 4.3-1.1 7.5-4.9 7.5-9.5V6z"/><path d="m8.8 12.2 2.3 2.3 4.3-4.6"/></svg></span>' +
      '<p class="aa-ch__eyebrow">Verificación de seguridad</p><h1 class="aa-ch__title">Un último paso</h1>';
    box.prepend(head);
    const steps = document.createElement('ol');
    steps.className = 'aa-ch__steps';
    steps.innerHTML = '<li><b>1</b><span>Marca la casilla</span></li><li><b>2</b><span>Se envía solo</span></li><li><b>3</b><span>Vuelves al artículo</span></li>';
    form?.before(steps);
    if (button) {
      if (button.tagName === 'INPUT') button.value = 'Continuar';
      else button.textContent = 'Continuar';
    }
    const back = document.createElement('a');
    let backUrl = '/';
    try {
      if (document.referrer && new URL(document.referrer).origin === location.origin) backUrl = document.referrer;
    } catch (error) {}
    back.href = backUrl;
    back.className = 'aa-ch__back';
    back.textContent = '← Volver sin enviar';
    box.append(back);
    // Continue automatically once the widget is solved.
    const timer = setInterval(() => {
      const token = form?.querySelector('[name="h-captcha-response"], [name="g-recaptcha-response"]');
      if (!token || !token.value) return;
      clearInterval(timer);
      box.classList.add('is-verified');
      if (button) {
        if (button.tagName === 'INPUT') button.value = 'Verificado · volviendo…';
        else button.textContent = 'Verificado · volviendo…';
      }
      setTimeout(() => (form.requestSubmit ? form.requestSubmit() : form.submit()), 450);
    }, 400);
  }

  // ---- Judge.me write-review modal: closing it (X, click outside or Esc) asks first, unless the review
  // was already sent. Judge.me's own close handler runs only after the shopper confirms.
  function initReviewModalGuard() {
    const html = document.documentElement;
    if (html.dataset.aaJmGuard) return;
    html.dataset.aaJmGuard = '1';
    let bypass = false;
    const star = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z"/></svg>';
    const modalOf = (el) => el?.closest?.('.jdgm-write-review-modal');
    const isOpen = (modal) => {
      const content = modal?.querySelector('.jdgm-write-review-modal__content');
      return Boolean(content && content.getClientRects().length && getComputedStyle(modal).display !== 'none');
    };
    const isSent = (modal) => Boolean(modal.querySelector('.jdgm-write-review-modal__section--page--thank-you.current-section'));
    const closeModal = (modal) => {
      bypass = true;
      try {
        modal.querySelector('.jdgm-write-review-modal__close-btn')?.click();
      } finally {
        bypass = false;
      }
    };
    const ask = (modal) => {
      if (modal.querySelector('.aa-jm-confirm')) return;
      const box = document.createElement('div');
      box.className = 'aa-jm-confirm';
      box.setAttribute('role', 'alertdialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-labelledby', 'AaJmConfirmTitle');
      box.innerHTML =
        `<div class="aa-jm-confirm__card"><div class="aa-jm-confirm__stars">${star.repeat(5)}</div>` +
        '<h3 class="aa-jm-confirm__title" id="AaJmConfirmTitle">¿Te vas sin opinar?</h3>' +
        '<p class="aa-jm-confirm__text">Tu reseña ayuda a otros aventureros a elegir mejor, y te toma menos de un minuto.</p>' +
        '<div class="aa-jm-confirm__actions"><button type="button" class="aa-jm-confirm__stay">Seguir con mi reseña</button><button type="button" class="aa-jm-confirm__leave">Salir de todos modos</button></div></div>';
      const dismiss = (leave) => {
        box.classList.add('is-out');
        setTimeout(() => {
          box.remove();
          if (leave) closeModal(modal);
          else modal.querySelector('.jdgm-write-review-modal__content input, .jdgm-write-review-modal__content textarea, .jdgm-star')?.focus?.({ preventScroll: true });
        }, 180);
      };
      box.addEventListener('click', (event) => {
        const t = event.target instanceof Element ? event.target : null;
        if (t?.closest('.aa-jm-confirm__leave')) dismiss(true);
        else if (t?.closest('.aa-jm-confirm__stay') || t === box) dismiss(false);
        event.stopPropagation();
      });
      ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'mouseup'].forEach((type) => box.addEventListener(type, (e) => e.stopPropagation()));
      box.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          dismiss(false);
        }
      });
      modal.append(box);
      box.querySelector('.aa-jm-confirm__stay')?.focus({ preventScroll: true });
    };
    // Capture phase on pointer/mouse/click so Judge.me's listeners never see the first attempt to close.
    const guard = (event) => {
      if (bypass) return;
      const t = event.target instanceof Element ? event.target : null;
      const modal = modalOf(t);
      if (!modal || !isOpen(modal) || isSent(modal) || t.closest('.aa-jm-confirm')) return;
      const isClose = t.closest('.jdgm-write-review-modal__close-btn');
      const isOutside = t.classList.contains('jdgm-write-review-modal__overlay') || t.classList.contains('jdgm-write-review-modal__container');
      if (!isClose && !isOutside) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.type === 'click') ask(modal);
    };
    ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'mouseup', 'click'].forEach((type) => document.addEventListener(type, guard, { capture: true, passive: false }));
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || bypass) return;
        const modal = [...document.querySelectorAll('.jdgm-write-review-modal')].find(isOpen);
        if (!modal || isSent(modal) || modal.querySelector('.aa-jm-confirm')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        ask(modal);
      },
      true
    );
  }

  // ---- Document pages (sections/aa-page-doc): index built from the H2 headings with the current one
  // highlighted while reading, and a print / save-as-PDF button.
  function initDoc(root = document) {
    root.querySelectorAll('[data-aa-doc]:not([data-aa-ready])').forEach((doc) => {
      doc.dataset.aaReady = '1';
      doc.querySelector('[data-aa-doc-print]')?.addEventListener('click', () => window.print());
      const list = doc.querySelector('[data-aa-doc-toc]');
      const heads = [...doc.querySelectorAll('[data-aa-doc-body] h2')];
      if (!list) return;
      if (!heads.length) {
        list.closest('.aa-doc__toc')?.remove();
        doc.querySelector('.aa-doc__grid')?.classList.add('aa-doc__grid--notoc');
        return;
      }
      const slug = (t) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 60);
      const links = heads.map((h, i) => {
        if (!h.id) h.id = `${slug(h.textContent) || 'seccion'}-${i + 1}`;
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.textContent = h.textContent.trim();
        a.setAttribute('data-no-transition', '');
        li.append(a);
        list.append(li);
        return a;
      });
      // On phones the index starts collapsed.
      const box = doc.querySelector('[data-aa-doc-tocbox]');
      const desk = window.matchMedia('(min-width: 990px)');
      const syncBox = () => {
        if (box && window.innerWidth > 0) box.open = desk.matches;
      };
      syncBox();
      desk.addEventListener('change', syncBox);
      list.addEventListener('click', (event) => {
        const a = event.target instanceof Element ? event.target.closest('a') : null;
        if (!a) return;
        event.preventDefault();
        document.getElementById(a.hash.slice(1))?.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', a.hash);
        if (box && window.matchMedia('(max-width: 989px)').matches) box.open = false;
      });
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const i = heads.indexOf(entry.target);
            links.forEach((a, n) => a.classList.toggle('is-active', n === i));
          }
        },
        { rootMargin: '-15% 0px -70% 0px' }
      );
      heads.forEach((h) => io.observe(h));
    });
  }

  // ---- Help center (sections/aa-help-center): filters the topic cards as you type; quick chips fill the box.
  function initHelp(root = document) {
    root.querySelectorAll('[data-aa-help]:not([data-aa-ready])').forEach((help) => {
      help.dataset.aaReady = '1';
      const input = help.querySelector('[data-aa-help-input]');
      const items = [...help.querySelectorAll('[data-aa-help-item]')];
      const empty = help.querySelector('[data-aa-help-empty]');
      const norm = (t) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
      const apply = () => {
        const words = norm(input?.value || '').split(/\s+/).filter(Boolean);
        let shown = 0;
        items.forEach((item) => {
          const text = `${item.dataset.keywords || ''} ${item.textContent}`;
          const hay = norm(text);
          // Synonyms and plurals too ("reembolso" finds "devoluciones", "despacho" finds "envíos")
          const ok = !words.length || words.every((w) => hay.includes(w)) || Boolean(window.AASearch?.matchesText(input.value, text));
          item.hidden = !ok;
          if (ok) shown += 1;
        });
        if (empty) empty.hidden = shown > 0;
      };
      input?.addEventListener('input', apply);
      help.querySelectorAll('[data-aa-help-chip]').forEach((chip) =>
        chip.addEventListener('click', () => {
          if (!input) return;
          const same = input.value === chip.dataset.aaHelpChip;
          input.value = same ? '' : chip.dataset.aaHelpChip;
          help.querySelectorAll('[data-aa-help-chip]').forEach((c) => c.classList.toggle('is-on', !same && c === chip));
          apply();
          input.focus({ preventScroll: true });
        })
      );
    });
  }

  // Velocidad: las animaciones infinitas de una sección fuera de pantalla se pausan (nadie las ve)
  // y siguen desde el mismo punto al acercarse. Sin esto el navegador recalcula estilos 60 veces por segundo.
  let offscreenObserver;
  let offscreenElObserver;
  const offscreenPaused = new WeakSet();
  const offscreenEls = new WeakSet(); // contenedores de animaciones que están fuera de pantalla
  // Se vigila el contenedor y no el elemento: si el elemento se mueve con su propia animación,
  // podría quedar pausado justo fuera del recorte y no volver nunca.
  const boxOf = (el) => el?.parentElement || el;
  const isLoop = (anim) => anim.effect?.getTiming().iterations === Infinity;
  const pauseAnim = (anim) => {
    if (anim.playState === 'running' && isLoop(anim)) { offscreenPaused.add(anim); anim.pause(); }
  };
  const resumeAnim = (anim) => {
    if (offscreenPaused.has(anim)) { offscreenPaused.delete(anim); anim.play(); }
  };
  function initPauseOffscreen(root = document) {
    if (!('IntersectionObserver' in window) || !Element.prototype.getAnimations) return;
    offscreenObserver ||= new IntersectionObserver((entries) => {
      for (const entry of entries) {
        for (const anim of entry.target.getAnimations({ subtree: true })) {
          if (!entry.isIntersecting) pauseAnim(anim);
          else if (!offscreenEls.has(boxOf(anim.effect?.target))) resumeAnim(anim);
        }
      }
    }, { rootMargin: '300px 0px' });
    const sections = root.matches?.('.shopify-section') ? [root] : root.querySelectorAll('.shopify-section');
    sections.forEach((section) => offscreenObserver.observe(section));
    // Secciones más altas que la pantalla (ficha, rieles del inicio): además se vigila cada elemento
    // con animación infinita, para pausar las que quedan fuera de la vista dentro de una sección visible.
    offscreenElObserver ||= new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) offscreenEls.delete(entry.target); else offscreenEls.add(entry.target);
        for (const anim of entry.target.getAnimations({ subtree: true })) {
          if (!entry.isIntersecting) pauseAnim(anim);
          else if (!offscreenEls.has(boxOf(anim.effect?.target))) resumeAnim(anim);
        }
      }
    }, { rootMargin: '200px 0px' });
    const watchEls = () => {
      for (const anim of document.getAnimations()) {
        const el = anim.effect?.target;
        if (el && isLoop(anim) && (root === document || root.contains(el))) offscreenElObserver.observe(boxOf(el));
      }
    };
    if (document.readyState === 'complete') setTimeout(watchEls, 600);
    else addEventListener('load', () => setTimeout(watchEls, 600), { once: true });
  }

  const initAll = (root = document) => {
    initDoc(root);
    initHelp(root);
    initComments(root);
    initShare(root);
    initReviews(root);
    initReveal(root);
    initStars(root);
    initCounters(root);
    initArticle(root);
    initPauseOffscreen(root);
  };

  /* Boleta o factura en el carrito: guarda la elección y los datos como atributos del carrito
     (llegan al pedido). Al pulsar Comprar con factura valida los datos y espera el guardado. */
  const initDteChoice = () => {
    let timer = null;
    let pending = Promise.resolve();
    let resuming = false;
    const isFactura = (box) => box.querySelector('input[value="Factura"]').checked;
    const rutOk = (raw) => {
      const v = raw.replace(/[.\s-]/g, '').toUpperCase();
      if (!/^\d{7,8}[\dK]$/.test(v)) return false;
      let sum = 0, mul = 2;
      for (let i = v.length - 2; i >= 0; i--) { sum += +v[i] * mul; mul = mul === 7 ? 2 : mul + 1; }
      const dv = 11 - (sum % 11);
      return v.slice(-1) === (dv === 11 ? '0' : dv === 10 ? 'K' : String(dv));
    };
    const save = (box) => {
      clearTimeout(timer); timer = null;
      const fact = isFactura(box);
      const attributes = { Documento: fact ? 'Factura' : 'Boleta' };
      box.querySelectorAll('[data-aa-attr]').forEach((i) => { attributes[i.dataset.aaAttr] = fact ? i.value.trim() : ''; });
      pending = fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }), keepalive: true,
      }).catch(() => {});
      return pending;
    };
    const sync = (box) => {
      // Mismos valores en el cajón y en la página del carrito.
      document.querySelectorAll('[data-aa-dte]').forEach((other) => {
        if (other === box) return;
        other.querySelector(`input[value="${isFactura(box) ? 'Factura' : 'Boleta'}"]`).checked = true;
        other.querySelector('.aa-dte__fields').hidden = !isFactura(box);
        box.querySelectorAll('[data-aa-attr]').forEach((i) => {
          const twin = other.querySelector(`[data-aa-attr="${i.dataset.aaAttr}"]`);
          if (twin) twin.value = i.value;
        });
      });
    };
    const check = (box) => {
      if (!isFactura(box)) return true;
      const err = box.querySelector('.aa-dte__error');
      let first = null;
      box.querySelectorAll('[data-aa-attr]').forEach((i) => {
        const bad = i.dataset.aaAttr === 'RUT' ? !rutOk(i.value) : !i.value.trim();
        i.setAttribute('aria-invalid', bad ? 'true' : 'false');
        if (bad && !first) first = i;
      });
      err.hidden = !first;
      if (first) {
        err.textContent = first.dataset.aaAttr === 'RUT' && first.value.trim() ? 'Revisa el RUT de la empresa.' : 'Completa los datos de la factura.';
        first.focus();
      }
      return !first;
    };
    document.addEventListener('change', (e) => {
      const box = e.target.closest?.('[data-aa-dte]');
      if (!box) return;
      if (e.target.type === 'radio') box.querySelector('.aa-dte__fields').hidden = !isFactura(box);
      sync(box);
      save(box);
    });
    document.addEventListener('input', (e) => {
      const box = e.target.closest?.('[data-aa-dte]');
      if (!box || !e.target.dataset.aaAttr) return;
      e.target.removeAttribute('aria-invalid');
      clearTimeout(timer);
      timer = setTimeout(() => { sync(box); save(box); }, 500);
    });
    document.addEventListener('click', (e) => {
      const btn = e.target.closest?.('.cart__checkout-button');
      if (!btn || resuming) return;
      const box = btn.closest('.cart-summary, cart-drawer-component, main')?.querySelector('[data-aa-dte]') || document.querySelector('[data-aa-dte]');
      if (!box) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!check(box)) return;
      (timer ? save(box) : pending).then(() => {
        resuming = true;
        btn.click();
        resuming = false;
      });
    }, true);
  };

  const ready = () => {
    initTopbar();
    initPageTransition();
    initNav();
    initSearch();
    initFavorites();
    initCartBubbleSync();
    initCartDrawerDismiss();
    initQuickAdd();
    initCollectionTools();
    initChallenge();
    initReviewModalGuard();
    initDteChoice();
    initAll();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
